import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import CreditTransfer from '../components/CreditTransfer';

export default function Credits() {
  const { tenantId, saasId, paasId } = useParams();
  const { user } = useSelector(state => state.auth);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('wallet');
  const [wallet, setWallet] = useState(null);
  const [transfers, setTransfers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [packs, setPacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showPurchase, setShowPurchase] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);

  const ownerId = tenantId || saasId || paasId || user?._id;
  const ownerType = tenantId ? 'tenant' : saasId ? 'saas' : paasId ? 'paas' : user?.role === 'superadmin' ? 'superadmin' : 'user';
  const walletRef = useRef(wallet);

  useEffect(() => {
    walletRef.current = wallet;
  }, [wallet]);

  useEffect(() => {
    loadWallet();
    loadPacks();
  }, [ownerId, ownerType]);

  useEffect(() => {
    if (wallet) {
      loadTransfers();
      loadStats();
      loadPending();
    }
  }, [wallet]);

  useEffect(() => {
    const pollWallet = setInterval(() => {
      loadWallet();
    }, 10000);

    const pollTransactions = setInterval(() => {
      if (walletRef.current) {
        loadTransfers();
        loadTransactions();
      }
    }, 15000);

    const pollPacks = setInterval(() => {
      loadPacks();
    }, 30000);

    return () => {
      clearInterval(pollWallet);
      clearInterval(pollTransactions);
      clearInterval(pollPacks);
    };
  }, []);

  const loadWallet = async () => {
    try {
      const res = await api.get(`/credits/wallet/balance/${ownerType}/${ownerId}`);
      setWallet(res.data.data);
    } catch (error) {
      if (error.response?.status === 404) {
        try {
          const createRes = await api.post('/credits/wallet', { ownerId, ownerType, initialBalance: 0 });
          setWallet(createRes.data.data);
        } catch (e) {
          console.error('Failed to create wallet:', e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const loadTransfers = async () => {
    try {
      const res = await api.get(`/credits/transfer/wallet/${wallet.walletId}`);
      setTransfers(res.data.data.transfers || []);
    } catch (error) {
      console.error('Failed to load transfers:', error);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get(`/credits/transfer/stats/${ownerType}/${ownerId}`);
      setStats(res.data.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadPending = async () => {
    try {
      const res = await api.get(`/credits/transfer/pending/${ownerType}/${ownerId}`);
      setPendingApprovals(res.data.data.transfers || []);
    } catch (error) {
      console.error('Failed to load pending:', error);
    }
  };

  const loadTransactions = async (type) => {
    try {
      const params = new URLSearchParams({ page: 1, limit: 50 });
      if (type) params.append('type', type);
      const res = await api.get(`/credits/transaction/wallet/${wallet.walletId}?${params}`);
      setTransactions(res.data.data.transactions || []);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    }
  };

  const loadPacks = async () => {
    try {
      const res = await api.get('/credits/pack');
      setPacks(res.data.data);
    } catch (error) {
      console.error('Failed to load packs:', error);
    }
  };

  const handlePurchase = async (packId) => {
    try {
      await api.post(`/credits/pack/${packId}/purchase`, { ownerId, ownerType });
      alert('Purchase initiated - connect payment gateway to complete');
      setShowPurchase(false);
      loadWallet();
    } catch (error) {
      alert(error.response?.data?.message || 'Purchase failed');
    }
  };

  const handleApprove = async (transferId) => {
    try {
      await api.patch(`/credits/transfer/${transferId}/approve`, { approverId: user._id });
      loadWallet();
      loadPending();
    } catch (error) {
      alert(error.response?.data?.message || 'Approval failed');
    }
  };

  const handleReject = async (transferId, reason) => {
    try {
      await api.patch(`/credits/transfer/${transferId}/reject`, { rejectedBy: user._id, reason });
      loadPending();
    } catch (error) {
      alert(error.response?.data?.message || 'Rejection failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'wallet', label: 'Wallet' },
    { id: 'transfers', label: 'Transfers' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'packs', label: 'Credit Packs' },
    { id: 'pending', label: `Pending (${pendingApprovals.length})` }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Credits Management</h1>
        <div className="space-x-2">
          <button onClick={() => setShowTransfer(true)} className="px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-600">
            Transfer Credits
          </button>
          <button onClick={() => setShowPurchase(true)} className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600">
            Purchase Pack
          </button>
        </div>
      </div>

      {wallet && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm text-gray-500">Balance</p>
            <p className="text-2xl font-bold text-primary-600">{wallet.balance?.toLocaleString()}</p>
            <p className="text-xs text-gray-400">{wallet.currency}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm text-gray-500">Available</p>
            <p className="text-2xl font-bold text-green-600">{wallet.availableBalance?.toLocaleString()}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm text-gray-500">Reserved</p>
            <p className="text-2xl font-bold text-orange-600">{wallet.reservedBalance?.toLocaleString()}</p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-sm text-gray-500">Status</p>
            <p className="text-xl font-bold capitalize">{wallet.status}</p>
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-500">This Month Outbound</p>
            <p className="text-lg font-bold">{stats.outbound?.thisMonth?.amount?.toLocaleString() || 0}</p>
            <p className="text-xs text-gray-400">{stats.outbound?.thisMonth?.count || 0} transfers</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-500">Today Outbound</p>
            <p className="text-lg font-bold">{stats.outbound?.today?.amount?.toLocaleString() || 0}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-500">Total Received</p>
            <p className="text-lg font-bold text-green-600">{stats.inbound?.allTime?.amount?.toLocaleString() || 0}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow">
        <div className="border-b">
          <nav className="flex space-x-6 px-4">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'wallet' && wallet && (
            <div className="space-y-4">
              <h3 className="font-semibold">Wallet Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Auto Recharge</label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={wallet.autoRecharge?.enabled || false}
                      className="h-4 w-4 text-primary-600 rounded"
                      readOnly
                    />
                    <span className="ml-2 text-sm text-gray-500">
                      Threshold: {wallet.autoRecharge?.threshold || 100} | Amount: {wallet.autoRecharge?.amount || 500}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Rollover</label>
                  <div className="mt-1 flex items-center">
                    <input
                      type="checkbox"
                      checked={wallet.rollover?.enabled || false}
                      className="h-4 w-4 text-primary-600 rounded"
                      readOnly
                    />
                    <span className="ml-2 text-sm text-gray-500">
                      {wallet.rollover?.percentage || 80}% monthly
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'transfers' && (
            <div>
              {transfers.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No transfers found</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b">
                      <th className="pb-3">Ref</th>
                      <th className="pb-3">Type</th>
                      <th className="pb-3">Amount</th>
                      <th className="pb-3">Direction</th>
                      <th className="pb-3">Status</th>
                      <th className="pb-3">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transfers.map(t => (
                      <tr key={t._id} className="border-b">
                        <td className="py-3 text-sm font-mono">{t.reference}</td>
                        <td className="py-3 text-sm capitalize">{t.type}</td>
                        <td className="py-3 text-sm font-bold">{t.amount?.toLocaleString()}</td>
                        <td className="py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${t.direction === 'inbound' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {t.direction}
                          </span>
                        </td>
                        <td className="py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${
                            t.status === 'completed' ? 'bg-green-100 text-green-700' :
                            t.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            t.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
                          }`}>{t.status}</span>
                        </td>
                        <td className="py-3 text-sm text-gray-500">{new Date(t.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              <div className="flex gap-2 mb-4">
                <button onClick={() => loadTransactions()} className="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200">All</button>
                <button onClick={() => loadTransactions('credit')} className="px-3 py-1 text-xs bg-green-100 rounded hover:bg-green-200">Credits</button>
                <button onClick={() => loadTransactions('debit')} className="px-3 py-1 text-xs bg-red-100 rounded hover:bg-red-200">Debits</button>
              </div>
              {transactions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No transactions found</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b">
                      <th className="pb-3">Date</th>
                      <th className="pb-3">Type</th>
                      <th className="pb-3">Amount</th>
                      <th className="pb-3">Balance Before</th>
                      <th className="pb-3">Balance After</th>
                      <th className="pb-3">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map(tx => (
                      <tr key={tx._id} className="border-b">
                        <td className="py-3 text-sm">{new Date(tx.createdAt).toLocaleDateString()}</td>
                        <td className="py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs ${tx.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {tx.type}
                          </span>
                        </td>
                        <td className="py-3 text-sm font-bold">{tx.amount?.toLocaleString()}</td>
                        <td className="py-3 text-sm">{tx.balanceBefore?.toLocaleString()}</td>
                        <td className="py-3 text-sm">{tx.balanceAfter?.toLocaleString()}</td>
                        <td className="py-3 text-sm text-gray-500">{tx.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {activeTab === 'packs' && (
            <div>
              {packs.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No packs available</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                  {packs.map(pack => (
                    <div key={pack._id} className="border rounded-lg p-4 text-center hover:shadow-lg transition-shadow">
                      <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center text-2xl" style={{ backgroundColor: pack.color + '20', color: pack.color }}>
                        {pack.type === 'free' ? '0' : pack.type === 'basic' ? '5K' : pack.type === 'bronze' ? '10K' : pack.type === 'silver' ? '50K' : pack.type === 'gold' ? '100K' : '1M'}
                      </div>
                      <h3 className="font-semibold text-lg">{pack.name}</h3>
                      <p className="text-2xl font-bold my-2">
                        ${pack.price?.amount}
                        <span className="text-sm font-normal text-gray-500">/{pack.validity?.days || 365}d</span>
                      </p>
                      <p className="text-sm text-gray-500 mb-3">{pack.credits?.toLocaleString()} credits</p>
                      {pack.type !== 'free' && (
                        <button
                          onClick={() => handlePurchase(pack._id)}
                          className="w-full py-2 bg-primary-500 text-white text-sm rounded hover:bg-primary-600"
                        >
                          Purchase
                        </button>
                      )}
                      {pack.type === 'free' && (
                        <button
                          onClick={() => handlePurchase(pack._id)}
                          className="w-full py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                        >
                          Claim Free
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'pending' && (
            <div>
              {pendingApprovals.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No pending approvals</p>
              ) : (
                <div className="space-y-4">
                  {pendingApprovals.map(t => (
                    <div key={t._id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-semibold">{t.amount?.toLocaleString()} credits</p>
                          <p className="text-sm text-gray-500">
                            From: {t.fromRole} | Type: {t.type}
                          </p>
                          <p className="text-xs text-gray-400">{new Date(t.createdAt).toLocaleString()}</p>
                        </div>
                        <div className="space-x-2">
                          <button
                            onClick={() => handleApprove(t._id)}
                            className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => {
                              const reason = prompt('Rejection reason:');
                              if (reason) handleReject(t._id, reason);
                            }}
                            className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showTransfer && (
        <CreditTransfer
          wallet={wallet}
          ownerId={ownerId}
          ownerType={ownerType}
          onClose={() => setShowTransfer(false)}
          onSuccess={() => {
            setShowTransfer(false);
            loadWallet();
            loadTransfers();
          }}
        />
      )}
    </div>
  );
}